import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { m, AnimatePresence } from "framer-motion";
import { ImagePlus, RotateCcw, X, Loader2 } from "lucide-react";
import { useUploadStore } from "../stores/uploadStore";
import { useIntakeCopy } from "../i18n/useIntakeCopy";
import { SPRING_TOUCH } from "../motion/tokens";
import { cn } from "@/lib/utils";

interface Pending { id: string; file: File; preview: string; status: "uploading" | "error" }
const MAX_BYTES = 10 * 1024 * 1024;

async function uploadOne(file: File): Promise<string> {
  const fd = new FormData(); fd.append("file", file);
  const res = await fetch("/.netlify/functions/upload-reference", { method: "POST", body: fd });
  if (!res.ok) throw new Error("upload_failed");
  const { url } = await res.json();
  if (typeof url !== "string" || !url.startsWith("https://res.cloudinary.com/")) throw new Error("bad_url");
  return url;
}

export function RefDropzone({ refs, onAdd, onRemove, max, label, compact }: {
  refs: string[]; onAdd(url: string): void; onRemove(url: string): void; max: number; label: string; compact?: boolean;
}) {
  const { c, fmt } = useIntakeCopy();
  const { begin, end } = useUploadStore();
  const [pending, setPending] = useState<Pending[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const room = max - refs.length - pending.filter((p) => p.status === "uploading").length;

  const run = useCallback(async (p: Pending) => {
    begin();
    setPending((list) => list.map((x) => (x.id === p.id ? { ...x, status: "uploading" } : x)));
    try {
      const url = await uploadOne(p.file);
      onAdd(url);
      setPending((list) => list.filter((x) => x.id !== p.id));
      URL.revokeObjectURL(p.preview);
    } catch {
      setPending((list) => list.map((x) => (x.id === p.id ? { ...x, status: "error" } : x)));
    } finally {
      end();
    }
  }, [begin, end, onAdd]);

  const onDrop = useCallback((accepted: File[], rejected: FileRejection[]) => {
    const notes: string[] = [];
    if (rejected.length) notes.push(fmt(c.upload.tooBig, { n: rejected.length }));
    if (accepted.length > room) notes.push(fmt(c.upload.tooMany, { n: Math.max(0, room) }));
    setNotice(notes.join(" ") || null);
    const items: Pending[] = accepted.slice(0, Math.max(0, room)).map((file) => ({
      id: globalThis.crypto.randomUUID(), file, preview: URL.createObjectURL(file), status: "uploading",
    }));
    setPending((list) => [...list, ...items]);
    items.forEach(run);
  }, [room, run, c, fmt]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { "image/*": [] }, maxSize: MAX_BYTES, disabled: room <= 0, multiple: true,
  });

  return (
    <div className="space-y-2">
      <div {...getRootProps({
        className: cn("flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed text-sm transition-colors",
          compact ? "px-3 py-3" : "px-4 py-6",
          isDragActive ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10" : "border-[var(--color-border)] hover:border-[var(--color-text-muted)]",
          room <= 0 && "cursor-not-allowed opacity-50"),
      })}>
        <input {...getInputProps()} aria-label={label} />
        <ImagePlus className="h-4 w-4 text-[var(--color-text-muted)]" aria-hidden />
        <span className="text-[var(--color-text-muted)]">{label}</span>
      </div>
      {notice && <p className="text-xs text-[var(--color-warning)]">{notice}</p>}
      <m.ul layout className="flex flex-wrap gap-2">
        <AnimatePresence initial={false}>
          {refs.map((url) => (
            <m.li key={url} layout initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.6, opacity: 0 }} transition={SPRING_TOUCH}
              className="group relative h-16 w-16 overflow-hidden rounded-lg border border-[var(--color-border)]">
              <img src={url.replace("/upload/", "/upload/c_fill,w_128,h_128,q_auto,f_auto/")} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => onRemove(url)} aria-label={c.upload.remove}
                className="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100">
                <X className="h-3 w-3 text-white" />
              </button>
            </m.li>
          ))}
          {pending.map((p) => (
            <m.li key={p.id} layout initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }} transition={SPRING_TOUCH}
              className="relative h-16 w-16 overflow-hidden rounded-lg border border-[var(--color-border)]">
              <img src={p.preview} alt="" className="h-full w-full object-cover opacity-50" />
              <div className="absolute inset-0 grid place-items-center">
                {p.status === "uploading"
                  ? <Loader2 className="h-5 w-5 animate-spin text-[var(--color-primary)]" aria-label={c.upload.uploading} />
                  : <button type="button" onClick={() => run(p)} aria-label={c.upload.retry} className="rounded-full bg-black/70 p-1"><RotateCcw className="h-4 w-4 text-white" /></button>}
              </div>
            </m.li>
          ))}
        </AnimatePresence>
      </m.ul>
    </div>
  );
}
